import type { TFunction } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type MappedAssetSensor, SensorTypeCode } from '@/common/asset-sensor';
import { ROLE } from '@/common/role';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePermissionChecker } from '@/hooks/use-permission';

import { MeteoTimeSeries } from './charts/met/TimeSeries';
import { Statistics } from './charts/statistics';
import { TpsTimeSeries } from './charts/tps/TimeSeries';
import { TpsTimeSeriesVector } from './charts/tps/TimeSeriesVector';
import { EmptyChart } from './EmptyChart';

export type SensorChartTabsProps = {
	assetId: string;
	selectedSensor: MappedAssetSensor | null;
};
const SENSOR_TABS = {
	TIMESERIES: 'TIMESERIES',
	TIMESERIES_VECTOR: 'TIMESERIES_VECTOR',
	SECTION: 'SECTION',
	SPIRIT_LEVEL: 'SPIRIT_LEVEL',
	CUMULATIVE: 'CUMULATIVE',
	RATE_OF_CHANGE: 'RATE_OF_CHANGE',
	STATISTICS: 'STATISTICS',
} as const;
type SENSOR_TABS = (typeof SENSOR_TABS)[keyof typeof SENSOR_TABS];
const sensorTabsLabels = (t: TFunction): Record<string, string> => ({
	[SENSOR_TABS.TIMESERIES]: t('health.charts.timeseries'),
	[SENSOR_TABS.TIMESERIES_VECTOR]: t('health.charts.timeseriesVector'),
	[SENSOR_TABS.SECTION]: t('health.charts.section'),
	[SENSOR_TABS.SPIRIT_LEVEL]: t('health.charts.spiritLevel'),
	[SENSOR_TABS.CUMULATIVE]: t('health.charts.cumulative'),
	[SENSOR_TABS.RATE_OF_CHANGE]: t('health.charts.rateOfChange'),
	[SENSOR_TABS.STATISTICS]: t('health.charts.statistics'),
});
type SensorTabSettingRecord = {
	tab: SENSOR_TABS;
	component: React.ComponentType<{ assetId: string; selectedSensor: MappedAssetSensor }>;
	permission?: ROLE; // Optional permission required to view this tab
};
const sensorTabSettingsObj: Partial<Record<SensorTypeCode, SensorTabSettingRecord[]>> = {
	[SensorTypeCode.TPS]: [
		{ tab: SENSOR_TABS.TIMESERIES, component: TpsTimeSeries, permission: ROLE.SENSORS_TPS_TIMESERIES_READ },
		{
			tab: SENSOR_TABS.TIMESERIES_VECTOR,
			component: TpsTimeSeriesVector,
			permission: ROLE.SENSORS_TPS_TIMESERIES_READ,
		},
		{ tab: SENSOR_TABS.STATISTICS, component: Statistics },
	],
	[SensorTypeCode.MET]: [
		{ tab: SENSOR_TABS.TIMESERIES, component: MeteoTimeSeries, permission: ROLE.SENSORS_MET_TIMESERIES_READ },
		{ tab: SENSOR_TABS.STATISTICS, component: Statistics },
	],
};
export function SensorChartTabs({ assetId, selectedSensor }: SensorChartTabsProps) {
	const { t } = useTranslation();
	const { hasPermission } = usePermissionChecker();
	const [activeTab, setActiveTab] = useState<SENSOR_TABS | ''>('');
	const sensorTabSettings = (selectedSensor?.type && sensorTabSettingsObj[selectedSensor.type]) || [];
	const tabLabels = useMemo(() => sensorTabsLabels(t), [t]);

	// Determine which tabs should be shown based on permissions
	const availableTabs = sensorTabSettings.map((setting) => ({
		key: setting.tab,
		value: setting.tab,
		label: tabLabels[setting.tab],
		hidden: setting.permission ? !hasPermission(setting.permission) : false,
	}));

	// When the selected sensor changes, check if the current active tab is still available. If not, switch to the first available tab
	useEffect(() => {
		if (!selectedSensor) {
			setActiveTab('');
			return;
		}
		const isCurrentTabAvailable = availableTabs.some((tab) => tab.value === activeTab && !tab.hidden);
		// If the selected sensor type changes and the current tab is not available, switch to the first available tab
		if (!activeTab || !isCurrentTabAvailable) {
			const firstAvailableTab = availableTabs.find((tab) => !tab.hidden);
			setActiveTab(firstAvailableTab ? firstAvailableTab.value : '');
		}
	}, [selectedSensor, availableTabs[0], activeTab]);

	// If no sensor is selected, show a message prompting the user to select a sensor
	if (!selectedSensor) {
		return null;
	}

	// If there are no tabs available for the selected sensor type, show a message
	if (availableTabs.length === 0) {
		return <EmptyChart title={t('health.empty.noChartsForSensorType')} />;
	}

	return (
		<div className="h-full flex flex-col">
			<Tabs
				value={activeTab}
				onValueChange={(v) => setActiveTab(v as SENSOR_TABS)}
				className="flex-1 overflow-hidden gap-0 bg-card text-card-foreground"
			>
				<TabsList className="justify-start rounded-none border-b border-transparent bg-transparent p-0">
					{availableTabs.map((tab) => (
						<TabsTrigger
							key={tab.key}
							value={tab.value}
							className="rounded-none border-0 border-b-2 border-transparent data-[state=active]:border-primary! data-[state=active]:bg-transparent data-[state=active]:text-primary! bg-transparent! text-muted-foreground hover:text-primary! hover:cursor-pointer px-4 py-2"
							hidden={tab.hidden}
						>
							{tab.label}
						</TabsTrigger>
					))}
				</TabsList>
				{sensorTabSettings.map(({ tab, component: Component }) => (
					<TabsContent key={tab} value={tab} className="flex-1 overflow-auto p-6 m-0 bg-background text-foreground">
						<Component assetId={assetId} selectedSensor={selectedSensor} />
					</TabsContent>
				))}
			</Tabs>
		</div>
	);
}
